import { UserRole, UserStatus } from "@/types";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { useListContext } from "react-admin";
import { z } from "zod";
import { useGetSubscriptionPlans } from "../../queries";
import { UsersListFilter } from "../../types";
import { UsersFilterProps } from "./users-filter.types";

const initialFormState = {
  roles: [],
  statuses: [],
  subscriptionTiers: [],
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formSchema = z.object({
  roles: z.array(z.nativeEnum(UserRole)),
  statuses: z.array(z.nativeEnum(UserStatus)),
  subscriptionTiers: z.array(z.string()),
});

type FormData = z.infer<typeof formSchema>;

const UsersFilter: FC<UsersFilterProps> = ({ onModalClose }) => {
  const { subscriptionPlans } = useGetSubscriptionPlans();
  const [filtersFormData, setFiltersFormData] = useState<Partial<FormData>>({});

  const { filterValues, setFilters } = useListContext();

  const formData: FormData = {
    roles:
      filtersFormData.roles ?? filterValues.roles ?? initialFormState.roles,
    statuses:
      filtersFormData.statuses ??
      filterValues.statuses ??
      initialFormState.statuses,
    subscriptionTiers:
      filtersFormData.subscriptionTiers ??
      filterValues.planIds ??
      initialFormState.subscriptionTiers,
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: "roles" | "subscriptionTiers" | "statuses"
  ) => {
    const checked = event.target.checked;
    const fieldValue = event.target.name;

    let currentArray = formData[field] ? [...formData[field]!] : [];

    // Special handling for statuses as it has multiple values
    const valuesToProcess =
      field === "statuses"
        ? (JSON.parse(fieldValue) as UserStatus[])
        : [fieldValue];

    if (checked) {
      valuesToProcess.forEach((v) => {
        if (!currentArray.includes(v)) {
          currentArray.push(v);
        }
      });
    } else {
      currentArray = currentArray.filter(
        (val) => !valuesToProcess.includes(val)
      );
    }

    setFiltersFormData({ ...formData, [field]: currentArray });
  };

  const onSubmit = () => {
    const filters: Pick<UsersListFilter, "roles" | "planIds" | "statuses"> = {
      roles: formData.roles.length ? formData.roles : undefined,
      statuses: formData.statuses.length
        ? (formData.statuses as UsersListFilter["statuses"])
        : undefined,
      planIds: formData.subscriptionTiers.length
        ? formData.subscriptionTiers
        : undefined,
    };

    setFilters({ ...filterValues, ...filters });
    onModalClose();
  };

  return (
    <Stack spacing={2}>
      <Divider sx={{ borderColor: "#333" }} />

      <FormGroup>
        <Typography variant="h6" align="left">
          Role
        </Typography>
        <FormControlLabel
          name={UserRole.MUSE}
          label={"Woman"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.roles.includes(UserRole.MUSE)}
              onChange={(e) => handleCheckboxChange(e, "roles")}
            />
          }
        />
        <FormControlLabel
          name={UserRole.ALPHA}
          label={"Man"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.roles.includes(UserRole.ALPHA)}
              onChange={(e) => handleCheckboxChange(e, "roles")}
            />
          }
        />
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />

      <FormGroup>
        <Typography variant="h6" align="left">
          Status
        </Typography>
        <FormControlLabel
          name={JSON.stringify([UserStatus.ACTIVE])}
          label={"Active"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.statuses?.includes(UserStatus.ACTIVE)}
              onChange={(e) => handleCheckboxChange(e, "statuses")}
            />
          }
        />
        <FormControlLabel
          name={JSON.stringify([
            UserStatus.INACTIVE,
            UserStatus.NOT_FINISHED_PROFILE,
          ])}
          label={"Inactive"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={
                formData.statuses?.includes(UserStatus.INACTIVE) ||
                formData.statuses?.includes(UserStatus.NOT_FINISHED_PROFILE)
              }
              onChange={(e) => handleCheckboxChange(e, "statuses")}
            />
          }
        />
      </FormGroup>

      {subscriptionPlans?.length && (
        <>
          <Divider sx={{ borderColor: "#333" }} />

          <FormGroup>
            <Typography variant="h6" align="left">
              Subscription Tier
            </Typography>

            {subscriptionPlans.map(({ id, title }) => (
              <FormControlLabel
                key={id}
                name={id}
                label={title}
                labelPlacement="start"
                sx={{ ml: 0, justifyContent: "space-between" }}
                control={
                  <Checkbox
                    checked={formData.subscriptionTiers?.includes(id)}
                    onChange={(e) =>
                      handleCheckboxChange(e, "subscriptionTiers")
                    }
                  />
                }
              />
            ))}
          </FormGroup>
        </>
      )}

      <Divider sx={{ borderColor: "#333" }} />

      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={() => {
            setFiltersFormData(initialFormState);
          }}
        >
          Reset
        </Button>

        <Stack direction={"row"} spacing={2}>
          <Button variant="outlined" onClick={onModalClose}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            Filter
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export { UsersFilter };
